import { createFileRoute, Link } from "@tanstack/react-router";
import { GAMES } from "@/data/games";

export const Route = createFileRoute("/live")({
  head: () => ({
    meta: [
      { title: "Livestream Mode — Mr. B Quotes Games" },
      { name: "description", content: "Open any Mr. B Quotes game in livestream mode: oversized type, on-screen timer, and A/B/C/D answer codes for chat voting." },
      { property: "og:title", content: "Mr. B Quotes — Livestream Mode" },
      { property: "og:description", content: "Stream-ready quote games with chat-friendly answer codes." },
      { property: "og:url", content: "/live" },
    ],
    links: [{ rel: "canonical", href: "/live" }],
  }),
  component: LivePicker,
});

function LivePicker() {
  return (
    <div className="min-h-screen">
      <header className="border-b border-border/60">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-5">
          <Link to="/" className="font-display text-xl text-gold">Mr. B Quotes</Link>
          <span className="rounded-full bg-error/20 text-error border border-error/40 px-3 py-1 text-xs uppercase tracking-widest">● Live</span>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Stream Setup</div>
        <h1 className="mt-2 font-display text-4xl sm:text-6xl">Pick a game for the stream</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Each game opens in <strong className="text-foreground">Live Mode</strong>: bigger type for OBS, a visible round timer, and letter codes (A / B / C / D) so chat can vote in real time.</p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {GAMES.map((g) => (
            <Link
              key={g.slug}
              to="/games/$slug/play"
              params={{ slug: g.slug }}
              search={g.slug === "scramble" ? { level: 1, live: "1" } : { live: "1" }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-gold hover:shadow-gold transition"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display text-4xl text-gold/60 group-hover:text-gold">{g.numeral}</span>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Open in live</span>
              </div>
              <h2 className="mt-3 font-display text-2xl">{g.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
              <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest">
                <span className="text-gold/70">{g.type}</span>
                <Link
                  to="/games/$slug"
                  params={{ slug: g.slug }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-gold"
                >Hub →</Link>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card/60 p-6">
          <h3 className="font-display text-xl text-gold">Streamer tips</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc list-inside">
            <li>Use a browser source in OBS at 1920×1080 for crisp display type.</li>
            <li>Chat shouts a letter (A / B / C / D) — you click it on stream and reveal.</li>
            <li>True or False is a great 60-second cooldown game between bigger rounds.</li>
            <li>Add <code className="text-gold">?live=1</code> to any game URL to share a direct live link.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
